import { useState } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useUserContext } from "../contexts";
import AutoLogout from "../components/AutoLogout.js";
import StatusPage from "../components/StatusPage.js";

const formatErrorDetails = (err) => {
  if (!err) return "No error details available.";
  if (isRouteErrorResponse(err)) {
    const lines = [`Status: ${err.status} ${err.statusText || ""}`.trim()];
    if (err.data) {
      lines.push(
        `Data: ${typeof err.data === "string" ? err.data : JSON.stringify(err.data, null, 2)}`
      );
    }
    return lines.join("\n");
  }
  if (err instanceof Error) {
    return err.stack || `${err.name}: ${err.message}`;
  }
  return String(err);
};

const ErrorPage = () => {
  const error = useRouteError();
  const { user } = useUserContext();
  const [copied, setCopied] = useState(false);

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  if (!is404) console.error(error);

  const icon = is404 ? "bi-map" : "bi-exclamation-triangle";
  const title = is404 ? "Page Not Found" : "Something Went Wrong";
  const message = is404
    ? "The page you're looking for doesn't exist or has been moved."
    : "An unexpected error occurred. Please try again or contact support if the problem persists.";

  const handleCopy = () => {
    const text = formatErrorDetails(error);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      {user?.isAuthenticated && <AutoLogout />}
      <StatusPage
        icon={icon}
        title={title}
        badge={
          is404 && (
            <p className='text-muted fw-semibold mb-1' style={{ fontSize: "1.1rem" }}>
              404
            </p>
          )
        }
        footer={
          !is404 && (
            <details className='mt-3 text-start' style={{ maxWidth: "520px", width: "100%" }}>
              <summary
                className='text-muted small'
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                <i className='bi bi-bug me-1'></i>Show error details
              </summary>
              <div className='mt-2'>
                <div className='d-flex justify-content-end mb-1'>
                  <button
                    className='btn btn-sm btn-outline-secondary'
                    onClick={handleCopy}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <pre
                  className='bg-light border rounded p-3 mb-0'
                  style={{
                    fontSize: "0.75rem",
                    maxHeight: "200px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {formatErrorDetails(error)}
                </pre>
              </div>
            </details>
          )
        }
      >
        <p className='text-muted mt-2' style={{ maxWidth: "420px" }}>
          {message}
        </p>
      </StatusPage>
    </>
  );
};

export default ErrorPage;
